import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Container, Row, Col, Card, Button, Form, InputGroup, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaWallet, FaEthereum, FaExchangeAlt, FaAddressCard, FaClock, FaFire, FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';
import WalletConnect from '../components/wallet/WalletConnect';
import './Wallet.css';

const Wallet = () => {
  const { currentUser, refreshUserData } = useAuth();
  
  const [walletAddress, setWalletAddress] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [claimLoading, setClaimLoading] = useState(false);
  const [burnLoading, setBurnLoading] = useState(false);
  const [claimableTokens, setClaimableTokens] = useState(0);
  const [transactions, setTransactions] = useState([]);
  
  // Estados para quemar tokens
  const [burnAmount, setBurnAmount] = useState(0);
  const [showBurnConfirm, setShowBurnConfirm] = useState(false);
  const [burnAnimation, setBurnAnimation] = useState(false);

  useEffect(() => {
    if (currentUser && currentUser.walletAddress) {
      setWalletAddress(currentUser.walletAddress);
    }
    
    // Cargar tokens reclamables
    fetchClaimableTokens();
    // Cargar transacciones
    fetchTransactions();
  }, [currentUser]);

  const fetchClaimableTokens = async () => {
    try {
      // Actualizar tokens reclamables (esto mueve tokens BLUE antiguos a reclamables)
      const response = await axios.get('/api/users/tokens/claimable');
      setClaimableTokens(response.data.claimableTokens);
    } catch (error) {
      console.error('Error al cargar tokens reclamables:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionLoading(true);
      // Lógica para cargar el historial de transacciones (pendiente de implementar)
      // const response = await axios.get('/api/users/transactions');
      // setTransactions(response.data.transactions);
      setTransactions([]);
    } catch (error) {
      console.error('Error al cargar transacciones:', error);
    } finally {
      setTransactionLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleWalletUpdate = async () => {
    if (!walletAddress) {
      toast.error('Por favor ingresa una dirección de wallet válida');
      return;
    }

    setLocalLoading(true);
    try {
      await axios.put('/api/users/profile', { walletAddress });
      setIsEditing(false);
      toast.success('Dirección de wallet actualizada correctamente');
      refreshUserData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al actualizar la dirección de wallet');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleClaimTokens = async () => {
    // Verificar si hay tokens para reclamar
    if (claimableTokens <= 0) {
      toast.info('No tienes tokens disponibles para reclamar');
      return;
    }

    // Verificar si hay una wallet configurada
    if (!currentUser.walletAddress) {
      toast.warning('Necesitas configurar una dirección de wallet para reclamar tokens');
      return;
    }

    setClaimLoading(true);
    try {
      const response = await axios.post('/api/users/tokens/claim');
      
      if (response.data.success) {
        toast.success(`¡Has reclamado ${response.data.amount} tokens correctamente!`);
        setClaimableTokens(0); // Actualizar el estado local
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error('Error al procesar el reclamo de tokens');
      console.error(error);
    } finally {
      setClaimLoading(false);
    }
  };

  // Función para manejar el cambio en el input de cantidad a quemar
  const handleBurnAmountChange = (e) => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    setBurnAmount(value);
  };

  // Función para validar si se puede quemar la cantidad especificada
  const validateBurnTokens = () => {
    if (burnAmount <= 0) {
      toast.info('Ingresa una cantidad válida mayor a cero');
      return false;
    }

    if (burnAmount > currentUser.tokensRed) {
      toast.error(`No tienes suficientes tokens RED. Máximo disponible: ${currentUser.tokensRed}`);
      return false;
    }

    if (burnAmount > currentUser.tokensBlue) {
      toast.error(`No tienes suficientes tokens BLUE. Máximo disponible: ${currentUser.tokensBlue}`);
      return false;
    }

    return true;
  };

  // Función para abrir el modal de confirmación
  const openBurnConfirmation = () => {
    if (validateBurnTokens()) {
      setShowBurnConfirm(true);
    }
  };

  // Función para ejecutar la quema de tokens
  const handleBurnTokens = async () => {
    setBurnLoading(true);
    setBurnAnimation(true);

    try {
      const response = await axios.post('/api/users/tokens/burn', {
        amount: burnAmount
      });

      // Esperar a que termine la animación antes de cerrar el modal
      setTimeout(() => {
        if (response.data.success) {
          toast.success(`¡Has quemado ${burnAmount} tokens RED y ${burnAmount} tokens BLUE!`, {
            icon: "🔥"
          });
          
          // Actualizar los datos del usuario
          refreshUserData();
          
          // Resetear la cantidad
          setBurnAmount(0);
          setShowBurnConfirm(false);
          setBurnAnimation(false);
        } else {
          toast.error(response.data.message || 'Error al quemar tokens');
          setBurnAnimation(false);
        }
        setBurnLoading(false);
      }, 2000);
    } catch (error) {
      toast.error('Error al procesar la quema de tokens');
      console.error(error);
      setBurnLoading(false);
      setBurnAnimation(false);
    }
  };

  if (currentUser === null) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p>Cargando información de wallet...</p>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h1 className="mb-4 text-center">
        <FaWallet className="me-2" /> Mi Wallet
      </h1>

      <Row className="mb-4">
        <Col md={12} lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0"><FaAddressCard className="me-2" /> Mi Dirección de Wallet</h4>
            </Card.Header>
            <Card.Body>
              {!isEditing ? (
                <>
                  <p>Tu dirección de wallet actual:</p>
                  <Alert variant={currentUser.walletAddress ? "info" : "warning"}>
                    {currentUser.walletAddress || 'No has configurado una dirección de wallet'}
                  </Alert>
                  <Button 
                    variant="outline-primary" 
                    onClick={() => setIsEditing(true)}
                    className="mt-2"
                  >
                    {currentUser.walletAddress ? 'Cambiar dirección' : 'Configurar dirección'}
                  </Button>
                </>
              ) : (
                <>
                  <Form.Group>
                    <Form.Label>Ingresa tu dirección de Ethereum:</Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaEthereum /></InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="0x..."
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                      />
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Esta dirección se usará para reclamar tus tokens.
                    </Form.Text>
                  </Form.Group>
                  <div className="mt-3 d-flex gap-2">
                    <Button 
                      variant="primary" 
                      onClick={handleWalletUpdate}
                      disabled={localLoading}
                    >
                      {localLoading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => {
                        setIsEditing(false);
                        setWalletAddress(currentUser.walletAddress || '');
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </>
              )}
              <div className="mt-4">
                <h5>Conectar con WalletConnect</h5>
                <WalletConnect onWalletConnected={(address) => {
                  setWalletAddress(address);
                  setIsEditing(true);
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={12} lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0"><FaExchangeAlt className="me-2" /> Mis Tokens</h4>
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center">
                <Col xs={6}>
                  <div className="text-center p-3 rounded bg-light">
                    <h5>Tokens RED</h5>
                    <h2 className="text-danger">{currentUser.tokensRed || 0}</h2>
                    <small>Tokens para crear tareas</small>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-3 rounded bg-light">
                    <h5>Tokens BLUE</h5>
                    <h2 className="text-primary">{currentUser.tokensBlue || 0}</h2>
                    <small>Tokens por completar tareas</small>
                  </div>
                </Col>
              </Row>
              <div className="mt-4">
                <p>
                  Los tokens RED se reciben al pagar por tareas y los tokens BLUE 
                  se reciben al completar tareas. Ambos tipos pueden ser quemados para 
                  mantener el equilibrio del sistema.
                </p>
                <Button variant="outline-primary" href="/marketplace" className="w-100">
                  Ir al Marketplace
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Nueva sección para Quemar Tokens */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-danger text-white">
              <h4 className="mb-0"><FaFire className="me-2" /> Quemar Tokens</h4>
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={7}>
                  <p>
                    Puedes quemar tokens para mantener el equilibrio del sistema. Al quemar tokens, 
                    se eliminará la misma cantidad de tokens RED y BLUE de tu cuenta.
                  </p>
                  <Alert variant="warning">
                    <FaExclamationTriangle className="me-2" />
                    <strong>¡Advertencia!</strong> Esta acción es irreversible. Los tokens quemados no 
                    pueden ser recuperados.
                  </Alert>
                </Col>
                <Col md={5}>
                  <Card className="p-3 border-danger">
                    <Form.Group className="mb-3">
                      <Form.Label>Cantidad de tokens RED a quemar:</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max={Math.min(currentUser.tokensRed || 0, currentUser.tokensBlue || 0)}
                        value={burnAmount}
                        onChange={handleBurnAmountChange}
                      />
                    </Form.Group>
                    
                    <div className="d-flex align-items-center mb-3">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <span className="text-danger me-2">Tokens RED:</span>
                          <strong>{burnAmount}</strong>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="text-primary me-2">Tokens BLUE:</span>
                          <strong>{burnAmount}</strong>
                        </div>
                      </div>
                      <div className="burn-animation-container">
                        {burnAmount > 0 && (
                          <FaFire 
                            className="text-danger ms-3 burn-icon" 
                            size={24}
                          />
                        )}
                      </div>
                    </div>
                    
                    <Button 
                      variant="danger" 
                      className="w-100 d-flex align-items-center justify-content-center"
                      disabled={burnAmount <= 0 || burnAmount > Math.min(currentUser.tokensRed || 0, currentUser.tokensBlue || 0)}
                      onClick={openBurnConfirmation}
                    >
                      <FaTrashAlt className="me-2" />
                      Quemar Tokens
                    </Button>
                    
                    <div className="mt-2 text-center text-muted small">
                      {Math.min(currentUser.tokensRed || 0, currentUser.tokensBlue || 0) <= 0 ? (
                        'No tienes suficientes tokens para quemar'
                      ) : (
                        `Máximo disponible: ${Math.min(currentUser.tokensRed || 0, currentUser.tokensBlue || 0)} tokens`
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sección de Tokens Reclamables */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-success text-white">
              <h4 className="mb-0"><FaClock className="me-2" /> Tokens Reclamables</h4>
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={7}>
                  <p>
                    Los tokens BLUE que tienes por más de 15 días se convierten en tokens reclamables.
                    Estos tokens pueden ser transferidos a tu wallet de Ethereum.
                  </p>
                  {!currentUser.walletAddress && (
                    <Alert variant="warning">
                      <strong>¡Atención!</strong> Necesitas configurar una dirección de wallet para reclamar tus tokens.
                    </Alert>
                  )}
                </Col>
                <Col md={5} className="text-center">
                  <div className="p-4 rounded bg-light">
                    <h5>BLUE Reclamables</h5>
                    <h2 className="text-success">{claimableTokens || 0}</h2>
                    <Button 
                      variant="success" 
                      className="mt-3 w-100"
                      disabled={claimLoading || claimableTokens <= 0 || !currentUser.walletAddress}
                      onClick={handleClaimTokens}
                    >
                      {claimLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Procesando...
                        </>
                      ) : (
                        'Reclamar Tokens'
                      )}
                    </Button>
                    <small className="d-block mt-2 text-muted">
                      {claimableTokens <= 0 
                        ? 'No tienes tokens para reclamar' 
                        : `Disponible: ${claimableTokens} tokens`}
                    </small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-info text-white">
              <h4 className="mb-0">Historial de Transacciones</h4>
            </Card.Header>
            <Card.Body>
              {transactionLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="info" />
                  <p className="mt-2">Cargando transacciones...</p>
                </div>
              ) : transactions.length === 0 ? (
                <p className="text-center text-muted">
                  No hay transacciones disponibles en este momento.
                </p>
              ) : (
                <div className="table-responsive">
                  {/* Contenido de la tabla de transacciones cuando esté implementado */}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de confirmación para quemar tokens */}
      <Modal show={showBurnConfirm} onHide={() => !burnLoading && setShowBurnConfirm(false)} centered>
        <Modal.Header closeButton={!burnLoading}>
          <Modal.Title>
            <FaFire className="text-danger me-2" />
            Confirmar quema de tokens
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {burnAnimation ? (
            <div className="text-center py-5">
              <div className="burn-animation">
                <div className="tokens-to-burn mb-3">
                  <span className="token-bubble red">{burnAmount}</span>
                  <span className="token-bubble blue">{burnAmount}</span>
                </div>
                <div className="fire-animation">
                  <FaFire className="flame flame-1" />
                  <FaFire className="flame flame-2" />
                  <FaFire className="flame flame-3" />
                </div>
              </div>
              <h4 className="mt-4">Quemando tokens...</h4>
            </div>
          ) : (
            <>
              <p className="mb-4">
                Estás a punto de quemar permanentemente:
              </p>
              <div className="d-flex justify-content-around mb-4">
                <div className="text-center">
                  <div className="p-3 bg-light rounded-circle mb-2 mx-auto token-display red">
                    <span className="text-danger">{burnAmount}</span>
                  </div>
                  <div>Tokens RED</div>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-light rounded-circle mb-2 mx-auto token-display blue">
                    <span className="text-primary">{burnAmount}</span>
                  </div>
                  <div>Tokens BLUE</div>
                </div>
              </div>
              <Alert variant="danger">
                <FaExclamationTriangle className="me-2" />
                <strong>¡Advertencia!</strong> Esta acción es irreversible. ¿Estás seguro que deseas continuar?
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!burnAnimation && (
            <>
              <Button 
                variant="secondary" 
                onClick={() => setShowBurnConfirm(false)}
                disabled={burnLoading}
              >
                Cancelar
              </Button>
              <Button 
                variant="danger" 
                onClick={handleBurnTokens}
                disabled={burnLoading}
              >
                {burnLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <FaFire className="me-2" />
                    Confirmar y Quemar
                  </>
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Wallet;
